import { useAlert } from '@ads/components-react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import type { Canvas } from 'fabric';
import { useAtom, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useGetApiV1SsoimageId, usePutApiV1SsoimageId } from '@/shared/api/generated';
import { useAddElement } from '../hooks/useAddElement';
import { useBackgroundControls } from '../hooks/useBackgroundControls';
import { useCanvasEvents } from '../hooks/useCanvasEvents';
import { useCanvasHistory } from '../hooks/useCanvasHistory';
import { useCanvasSync } from '../hooks/useCanvasSync';
import { useObjectTransform } from '../hooks/useObjectTransform';
import { useSaveToLibrary } from '../hooks/useSaveToLibrary';
import { useSlideCanvasLoader } from '../hooks/useSlideCanvasLoader';
import { useSlideIO } from '../hooks/useSlideIO';
import { useVariantSwitch } from '../hooks/useVariantSwitch';
import { editorSelectionAtom } from '../store/atoms/editorSelectionAtom';
import { type ActiveLanguage, editorUiAtom } from '../store/atoms/editorUiAtom';
import { slideAtom, type VariantKey } from '../store/atoms/slideAtom';
import { slideCategoriesAtom } from '../store/atoms/slideCategoriesAtom';
import { slideEditorParamsAtom } from '../store/atoms/slideEditorParamsAtom';
import { slideLibraryAtom } from '../store/atoms/slideLibraryAtom';
import type { SlideEditorContextValue } from '../types/slideEditorContext';

export type {
  CanvasObjectMeta,
  FabricTextProps,
  SlideEditorContextValue,
  SlideElementType,
} from '../types/slideEditorContext';

const SlideEditorContext = createContext<SlideEditorContextValue | null>(null);
SlideEditorContext.displayName = 'SlideEditorContext';

export function useSlideEditorContext(): SlideEditorContextValue {
  const ctx = useContext(SlideEditorContext);
  if (!ctx) throw new Error('useSlideEditorContext must be used within <SlideEditorProvider>');
  return ctx;
}

export type SlideEditorProviderProps = PropsWithChildren<{ slideId?: number }>;

export function SlideEditorProvider({ children, slideId: routeSlideId }: SlideEditorProviderProps) {
  const [{ slideId: atomSlideId, categoryId: initialCategoryId }] = useAtom(slideEditorParamsAtom);
  const slideId = atomSlideId ?? routeSlideId;
  const desktopCanvasRef = useRef<Canvas | null>(null);
  const mobileCanvasRef = useRef<Canvas | null>(null);

  const [isTranslating] = useState(false);
  // Incrementado sempre que um canvas fica pronto. Usado como dep do effect de
  // carregamento do canvas para re-disparar quando o canvas monta após os dados da API.
  const [_canvasReadyTick, setCanvasReadyTick] = useState(0);
  // Incrementado após cada loadFromJSON completar (API, troca de device/idioma).
  const [canvasLoadVersion, setCanvasLoadVersion] = useState(0);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<number | undefined>(initialCategoryId);

  const [uiState, setUiState] = useAtom(editorUiAtom);
  const [slide, setSlide] = useAtom(slideAtom);
  const setSelection = useSetAtom(editorSelectionAtom);
  const setLibrarySlides = useSetAtom(slideLibraryAtom);

  const queryClient = useQueryClient();
  const { mutate: updateSsoimage, isPending: isSaving } = usePutApiV1SsoimageId();
  const { data: currentSlide, isFetching: isSlideFetching } = useGetApiV1SsoimageId(
    Number(slideId),
    { query: { placeholderData: keepPreviousData } },
  );
  const { alert } = useAlert();
  const [categories] = useAtom(slideCategoriesAtom);

  // Avisa o usuário antes de sair (refresh / fechar aba) com alterações não salvas.
  // O diálogo SPA já é tratado pelo useBlocker em SlideEditorTemplate.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Espelha o slide em uma ref para evitar closures obsoletas nos callbacks
  const slideRef = useRef(slide);
  useEffect(() => {
    slideRef.current = slide;
  }, [slide]);

  // Reseta o átomo ao trocar de slide para evitar que dados do slide anterior
  // bloqueiem o carregamento do novo via guard de variante.
  useEffect(() => {
    if (!slideId) return;
    setSlide(RESET);
  }, [slideId, setSlide]);

  const activeDevice = uiState.activeDevice;
  const activeLanguage = uiState.activeLanguage;
  const variantKey: VariantKey = `${activeLanguage}-${activeDevice}`;

  const getActiveCanvas = useCallback((): Canvas | null => {
    return activeDevice === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
  }, [activeDevice]);

  // Espelha o idioma ativo em uma ref para os listeners do canvas lerem o valor atual.
  const activeLanguageRef = useRef<ActiveLanguage>(activeLanguage);
  useEffect(() => {
    activeLanguageRef.current = activeLanguage;
  }, [activeLanguage]);

  // ─── Sub-hooks ──────────────────────────────────────────────────────────────

  const {
    pushSnapshot,
    resetHistory,
    updateHistoryState,
    undo: undoFn,
    redo: redoFn,
    canUndo,
    canRedo,
  } = useCanvasHistory({ desktopCanvasRef, mobileCanvasRef });

  const { saveCurrentSlide, generateThumbnail, syncSelectionAtom } = useCanvasSync({
    desktopCanvasRef,
    mobileCanvasRef,
    setSlide,
    setSelection,
  });

  const { addElement } = useAddElement({
    getActiveCanvas,
    saveCurrentSlide,
    pushSnapshot,
    variantKey,
    activeDevice,
    alert,
  });

  const {
    deleteSelected,
    duplicateSelected: duplicateSelectedFn,
    bringForward,
    sendBackward,
    setObjectPosition,
    setObjectSize,
    setObjectAngle,
    setObjectOpacity,
    setFill,
    setStroke,
    setTextProps,
    setBackground: setBackgroundRaw,
    setBackgroundImage: setBackgroundImageRaw,
    removeBackgroundImage: removeBackgroundImageRaw,
    selectObject,
    getObjects,
    setBannerCtaHref,
    getPreviewDataUrl,
  } = useObjectTransform({
    getActiveCanvas,
    syncSelectionAtom,
    saveCurrentSlide,
    variantKey,
    setSlide,
    setSelection,
    activeDevice,
    alert,
  });

  const {
    setBackground,
    setBackgroundImage,
    removeBackgroundImage,
    setBackgroundVideo,
    removeBackgroundVideo,
  } = useBackgroundControls({
    getActiveCanvas,
    variantKey,
    saveCurrentSlide,
    setSlide,
    slideRef,
    setIsDirty,
    setBackgroundRaw,
    setBackgroundImageRaw,
    removeBackgroundImageRaw,
  });

  const { canSave, getVariantCompleteness } = useSlideIO({ slide });

  const { isCanvasLoading } = useSlideCanvasLoader({
    slideId,
    currentSlide,
    isSlideFetching,
    activeLanguage,
    activeDevice,
    variantKey,
    slide,
    slideRef,
    getActiveCanvas,
    saveCurrentSlide,
    resetHistory,
    pushSnapshot,
    setSlide,
    setCanvasLoadVersion,
    setActiveCategoryId,
  });

  const { onDesktopCanvasReady, onMobileCanvasReady } = useCanvasEvents({
    desktopCanvasRef,
    mobileCanvasRef,
    activeLanguage,
    activeLanguageRef,
    syncSelectionAtom,
    setSelection,
    saveCurrentSlide,
    pushSnapshot,
    resetHistory,
    setIsDirty,
    setCanvasReadyTick,
  });

  const { setActiveDevice, setActiveLanguage } = useVariantSwitch({
    desktopCanvasRef,
    mobileCanvasRef,
    variantKey,
    activeDevice,
    activeLanguage,
    slideRef,
    saveCurrentSlide,
    generateThumbnail,
    setUiState,
    setSlide,
    setSelection,
    setTranslationError,
    setCanvasLoadVersion,
    resetHistory,
    updateHistoryState,
    pushSnapshot,
  });

  const saveToLibrary = useSaveToLibrary({
    slideId,
    variantKey,
    activeCategoryId,
    currentSlide,
    slideRef,
    queryClient,
    saveCurrentSlide,
    setLibrarySlides,
    updateSsoimage,
    alert,
    setIsDirty,
  });

  // ─── Undo / Redo (bound to current variantKey) ───────────────────────────────

  const undo = useCallback(() => {
    undoFn(variantKey, saveCurrentSlide);
  }, [undoFn, variantKey, saveCurrentSlide]);

  const redo = useCallback(() => {
    redoFn(variantKey, saveCurrentSlide);
  }, [redoFn, variantKey, saveCurrentSlide]);

  const duplicateSelected = useCallback(() => {
    duplicateSelectedFn(pushSnapshot);
  }, [duplicateSelectedFn, pushSnapshot]);

  // ─── Slide name / Category ───────────────────────────────────────────────────

  const slideName = slide.name;

  const setSlideName = useCallback(
    (name: string) => {
      setIsDirty(true);
      setSlide(prev => ({ ...prev, name }));
    },
    [setSlide],
  );

  const categoryName = useMemo(() => {
    return categories.find(c => c.id === activeCategoryId)?.name ?? '';
  }, [categories, activeCategoryId]);

  const setCategoryId = useCallback((id: number) => {
    setIsDirty(true);
    setActiveCategoryId(id);
  }, []);

  // ─── Display period ──────────────────────────────────────────────────────────

  const displayPeriod = useMemo(
    () => ({
      startDate: slide.displayPeriod?.startDate ?? '',
      endDate: slide.displayPeriod?.endDate ?? '',
    }),
    [slide],
  );

  const setDisplayPeriod = useCallback(
    (startDate: string, endDate: string) => {
      setIsDirty(true);
      setSlide(prev => {
        if (!startDate && !endDate) {
          return { id: prev.id, name: prev.name, variants: prev.variants };
        }
        const period: { startDate?: string; endDate?: string } = {};
        if (startDate) period.startDate = startDate;
        if (endDate) period.endDate = endDate;
        return { ...prev, displayPeriod: period };
      });
    },
    [setSlide],
  );

  // ─── Context value ───────────────────────────────────────────────────────────

  const value = useMemo<SlideEditorContextValue>(
    () => ({
      ...(slideId != null ? { slideId: Number(slideId) } : {}),
      onDesktopCanvasReady,
      onMobileCanvasReady,
      activeDevice,
      setActiveDevice,
      activeLanguage,
      setActiveLanguage,
      isTranslating,
      translationError,
      addElement,
      deleteSelected,
      duplicateSelected,
      bringForward,
      sendBackward,
      setObjectPosition,
      setObjectSize,
      setObjectAngle,
      setObjectOpacity,
      setFill,
      setStroke,
      setTextProps,
      setBackground,
      setBackgroundImage,
      removeBackgroundImage,
      setBackgroundVideo,
      removeBackgroundVideo,
      selectObject,
      getObjects,
      undo,
      redo,
      canUndo,
      canRedo,
      slideName,
      setSlideName,
      categoryName,
      categoryId: activeCategoryId,
      categories,
      setCategoryId,
      canSave,
      getVariantCompleteness,
      getPreviewDataUrl,
      setBannerCtaHref,
      saveToLibrary,
      isSaving,
      displayPeriod,
      setDisplayPeriod,
      isDirty,
      isCanvasLoading,
      canvasLoadVersion,
    }),
    [
      slideId,
      onDesktopCanvasReady,
      onMobileCanvasReady,
      activeDevice,
      setActiveDevice,
      activeLanguage,
      setActiveLanguage,
      isTranslating,
      translationError,
      addElement,
      deleteSelected,
      duplicateSelected,
      bringForward,
      sendBackward,
      setObjectPosition,
      setObjectSize,
      setObjectAngle,
      setObjectOpacity,
      setFill,
      setStroke,
      setTextProps,
      setBackground,
      setBackgroundImage,
      removeBackgroundImage,
      setBackgroundVideo,
      removeBackgroundVideo,
      selectObject,
      getObjects,
      undo,
      redo,
      canUndo,
      canRedo,
      slideName,
      setSlideName,
      categoryName,
      activeCategoryId,
      categories,
      setCategoryId,
      canSave,
      getVariantCompleteness,
      getPreviewDataUrl,
      setBannerCtaHref,
      saveToLibrary,
      isSaving,
      displayPeriod,
      setDisplayPeriod,
      isDirty,
      isCanvasLoading,
      canvasLoadVersion,
    ],
  );

  return <SlideEditorContext.Provider value={value}>{children}</SlideEditorContext.Provider>;
}
